import React, { useRef, useEffect, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../components/ui/button';
import { Camera, RefreshCw, AlertCircle } from 'lucide-react';

export default function CameraScanner({ onScan }) {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraId, setCameraId] = useState(null);
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const initScanner = async () => {
      try {
        setError(null);
        
        // Get available cameras
        const devices = await Html5Qrcode.getCameras();
        
        if (!isMounted) return;
        
        if (devices.length === 0) {
          setError('No camera found. Please ensure camera permissions are granted.');
          return;
        }

        setCameras(devices);

        // Prefer back camera on mobile
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        
        const selectedCamera = backCamera || devices[0];
        setCameraId(selectedCamera.id);

        // Initialize scanner
        const html5QrCode = new Html5Qrcode('camera-scanner-container');
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          selectedCamera.id,
          {
            fps: 10,
            qrbox: { width: 280, height: 150 },
            aspectRatio: 1.7778,
          },
          (decodedText) => {
            // Success callback
            if (isMounted) {
              // Stop scanner before calling onScan
              html5QrCode.stop().then(() => {
                html5QrCodeRef.current = null;
                onScan(decodedText);
              }).catch(console.error);
            }
          },
          (errorMessage) => {
            // Error callback - ignore scan failures (they're frequent)
            // Only log for debugging if needed
          }
        );

        if (isMounted) {
          setScanning(true);
        }
      } catch (err) {
        console.error('Camera initialization error:', err);
        if (isMounted) {
          if (err.message?.includes('Permission')) {
            setError('Camera permission denied. Please allow camera access in your browser settings.');
          } else if (err.message?.includes('not found')) {
            setError('Camera not found. Please connect a camera and try again.');
          } else {
            setError(`Failed to start camera: ${err.message || 'Unknown error'}`);
          }
          setScanning(false);
        }
      }
    };

    initScanner();

    return () => {
      isMounted = false;
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(console.error);
        html5QrCodeRef.current = null;
      }
    };
  }, [onScan]);

  const handleRetry = () => {
    window.location.reload();
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
      }

      // Find next camera
      const currentIndex = cameras.findIndex(c => c.id === cameraId);
      const nextIndex = (currentIndex + 1) % cameras.length;
      const nextCamera = cameras[nextIndex];
      
      setCameraId(nextCamera.id);
      setScanning(false);

      const html5QrCode = new Html5Qrcode('camera-scanner-container');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        nextCamera.id,
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.7778,
        },
        (decodedText) => {
          html5QrCode.stop().then(() => {
            html5QrCodeRef.current = null;
            onScan(decodedText);
          }).catch(console.error);
        },
        () => {}
      );

      setScanning(true);
    } catch (err) {
      console.error('Camera switch error:', err);
      setError('Failed to switch camera');
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-600 text-sm mb-3">{error}</p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRetry}
          data-testid="retry-camera-btn"
        >
          <Camera className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        id="camera-scanner-container" 
        className="relative bg-black rounded-lg overflow-hidden"
        style={{ minHeight: '200px' }}
        data-testid="camera-scanner-container"
      />
      
      {/* Camera switch button (if multiple cameras) */}
      {cameras.length > 1 && scanning && (
        <Button
          variant="outline"
          size="sm"
          onClick={switchCamera}
          className="absolute top-2 right-2 bg-white/80 hover:bg-white"
          data-testid="switch-camera-btn"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Switch
        </Button>
      )}
      
      {scanning && (
        <div className="text-center mt-2">
          <p className="text-xs text-gray-500">
            Point camera at barcode
          </p>
          {cameras.length > 1 && (
            <p className="text-xs text-gray-400 mt-1">
              Using: {cameras.find(c => c.id === cameraId)?.label || 'Camera'}
            </p>
          )}
        </div>
      )}
      
      {!scanning && !error && (
        <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
          <div className="text-center">
            <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-pulse" />
            <p className="text-sm text-gray-500">Starting camera...</p>
          </div>
        </div>
      )}
    </div>
  );
}
